// src/hooks/useHints.ts
import { useEffect, useState } from "react";

/**
 * Centralizes Hint visibility & toggle logic backed by localStorage.
 * Works for any page by passing the local keys and a storage prefix.
 */
export function useHints<T extends readonly string[]>(
  keys: T,
  storagePrefix: string
) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [hintsHidden, setHintsHidden] = useState<boolean>(() => {
    try {
      return (keys as readonly string[]).every(
        (k) => localStorage.getItem(`${storagePrefix}${k}`) === "dismissed"
      );
    } catch {
      return false;
    }
  });

  const markAll = (state: "dismissed" | null) => {
    (keys as readonly string[]).forEach((k) => {
      const key = `${storagePrefix}${k}`;
      try {
        if (state === "dismissed") localStorage.setItem(key, "dismissed");
        else localStorage.removeItem(key);
      } catch {}
    });
    setRefreshToken((t) => t + 1);
  };

  const toggleHints = () => {
    if (hintsHidden) {
      markAll(null);
      setHintsHidden(false);
    } else {
      markAll("dismissed");
      setHintsHidden(true);
    }
  };

  // Support "?reset-hints=1" for the given prefix (dev helper)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset-hints") === "1") {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || "";
        if (k.startsWith(storagePrefix)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
      setRefreshToken((t) => t + 1);
      setHintsHidden(false);
    }
  
  }, []);

  return { hintsHidden, toggleHints, refreshToken, markAll };
}
